import { ChevronDown } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'
import { MergeContactsDialog } from '../contact/merge/MergeContactsDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RefPicker } from '../filters/RefPicker'
import type { FilterOptions, RefKind } from '../filters/useFilterOptions'
import type { CrmObject, OptionDef, Register } from '../query/types'
import { ask, choicesOf } from './bulkHelpers'

export type BulkActionKey = 'labels' | 'category' | 'owner' | 'status' | 'list' | 'organisation' | 'delete' | 'pause' | 'end' | 'agreement'

/**
 * What a menu choice asks for: the route (`action`), its body next to the selection (`params`),
 * the Dutch text of the question ("1 contact: {summary}?") and whether to ask at all.
 */
export interface BulkRequest {
  action: BulkActionKey
  params: Record<string, unknown>
  summary: string
  confirm: boolean
  destructive: boolean
  /** Extra sentence in the confirmation. */
  note?: string
}

/** What the menus of a bulk bar get from the list page. */
export interface BulkMenusProps {
  register: Register
  options: FilterOptions
  /** The view lists archived records: the status button reactivates instead. */
  archived: boolean
  onRequest: (request: BulkRequest) => void
  onExport: () => void
  /** What is ticked, for a page with a bulk action of its own (Uit lijst halen). */
  selected?: { ids: string[]; all: boolean; total: number }
  /** After such an action: clear the ticks and read the rows again. */
  onDone?: () => void
}

/** Above this many choices the list gets a search field. */
const SEARCH_ABOVE = 8
const ROW = 'flex min-h-8 w-full items-center rounded-md px-2 py-1 text-left text-sm hover:bg-muted max-md:min-h-11'

export type Choose = (request: BulkRequest) => void

/** A button that opens a popover; what is chosen in it goes on as a request and closes it. */
export function MenuPopover({
  label,
  wide,
  onRequest,
  children,
}: {
  label: string
  wide?: boolean
  onRequest: Choose
  children: (choose: Choose) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const choose: Choose = (request) => {
    onRequest(request)
    setOpen(false)
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          {label}
          <ChevronDown aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label={label} className={wide ? 'w-80 p-2' : 'w-72 p-2'}>
        {children(choose)}
      </PopoverContent>
    </Popover>
  )
}

/**
 * The choices of a menu, with a search field when there are many. `lead` is a row above them that
 * the search does not hide ("Geen categorie"); `choices` is null while they load.
 */
export function ChoiceList({
  label,
  choices,
  lead,
  row,
}: {
  label: string
  choices: readonly OptionDef[] | null
  lead?: ReactNode
  row: (choice: OptionDef) => ReactNode
}) {
  const [term, setTerm] = useState('')
  const needle = term.trim().toLowerCase()
  const shown = choices?.filter((choice) => choice.label.toLowerCase().includes(needle)) ?? []
  return (
    <div className="flex flex-col gap-1">
      {choices && choices.length > SEARCH_ABOVE ? (
        <Input
          type="search"
          aria-label={`Zoek in ${label}`}
          placeholder="Zoeken…"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          className="max-md:h-11 max-md:text-base"
        />
      ) : null}
      {lead}
      <div className="flex max-h-64 flex-col overflow-y-auto">
        {choices === null ? (
          <p role="status" className="px-2 py-3 text-sm text-muted-foreground">
            Laden…
          </p>
        ) : choices.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Geen opties</p>
        ) : shown.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Geen resultaten</p>
        ) : (
          shown.map(row)
        )}
      </div>
    </div>
  )
}

export function ChoiceButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={ROW}>
      <span className="min-w-0 break-words">{children}</span>
    </button>
  )
}

/** One organisation to link the selection to, and the contacts' role there (the role is optional). */
function OrganisationForm({ options, choose }: { options: FilterOptions; choose: Choose }) {
  const [chosen, setChosen] = useState<{ id: string; name: string } | null>(null)
  const [role, setRole] = useState('')
  // The picker tells us the name of what was picked; the chosen organisation is the last one picked.
  const picker = {
    ...options,
    remember: (kind: RefKind, id: string, name: string) => {
      setChosen({ id, name })
      options.remember?.(kind, id, name)
    },
  }

  function link() {
    if (!chosen) return
    const given = role.trim()
    choose(ask('organisation', { organisation_id: chosen.id, role: given || null }, `koppelen aan ${chosen.name}${given ? ` als ${given}` : ''}`))
  }

  return (
    <div className="flex flex-col gap-3">
      <RefPicker
        kind="organisations"
        label="Organisatie"
        value={chosen ? [chosen.id] : []}
        options={picker}
        onChange={(next) => {
          if (next.length === 0) setChosen(null)
        }}
      />
      <Input aria-label="Functie" placeholder="Functie (optioneel)" maxLength={200} value={role} onChange={(event) => setRole(event.target.value)} className="max-md:h-11 max-md:text-base" />
      <Button type="button" size="sm" className="self-start" disabled={!chosen} onClick={link}>
        Koppelen
      </Button>
    </div>
  )
}

/** The owner to set; by default the agreements and open tasks of the records go along (ED-33). */
function OwnerChoices({ team, choose }: { team: OptionDef[] | null; choose: Choose }) {
  const [transfer, setTransfer] = useState(true)
  const id = useId()
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="flex items-center gap-2 px-2 text-sm">
        <input id={id} type="checkbox" className="size-4 accent-primary" checked={transfer} onChange={(event) => setTransfer(event.target.checked)} />
        Ook afspraken en open taken overdragen
      </label>
      <ChoiceList
        label="Eigenaar"
        choices={team}
        lead={<ChoiceButton onClick={() => choose(ask('owner', { owner_user_id: null, transfer_agreements: transfer }, 'eigenaar weghalen'))}>Geen eigenaar</ChoiceButton>}
        row={(choice) => (
          <ChoiceButton
            key={choice.value}
            onClick={() => choose(ask('owner', { owner_user_id: choice.value, transfer_agreements: transfer }, `eigenaar ${choice.label} zetten`))}
          >
            {choice.label}
          </ChoiceButton>
        )}
      />
    </div>
  )
}

/** "Minstens elke [1] [maand] contact": a contact rhythm for every ticked contact (design §5.6). */
function RhythmForm({ choose }: { choose: Choose }) {
  const [count, setCount] = useState('1')
  const [unit, setUnit] = useState<'week' | 'maand' | 'jaar'>('maand')
  const amount = Number(count)
  const valid = Number.isInteger(amount) && amount >= 1 && amount <= 99
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 text-sm">
        Minstens elke
        <Input
          aria-label="Hoe vaak"
          type="number"
          min={1}
          max={99}
          value={count}
          onChange={(event) => setCount(event.target.value)}
          className="w-16 max-md:h-11 max-md:text-base"
        />
        <select
          aria-label="Eenheid"
          value={unit}
          onChange={(event) => setUnit(event.target.value as 'week' | 'maand' | 'jaar')}
          className="h-8 rounded-md border border-border bg-background px-2 text-sm max-md:h-11 max-md:text-base"
        >
          <option value="week">week</option>
          <option value="maand">maand</option>
          <option value="jaar">jaar</option>
        </select>
        contact
      </div>
      <Button
        type="button"
        size="sm"
        className="self-start"
        disabled={!valid}
        onClick={() => choose(ask('agreement', { interval_count: amount, interval_unit: unit }, `contactritme elke ${amount} ${unit} instellen`))}
      >
        Instellen
      </Button>
    </div>
  )
}

/**
 * The menus of the bulk bar (design §5.6), in the order of the design: Labels, Categorie, Eigenaar,
 * (contacts: Aan lijst, Organisatie koppelen), Archiveren or Heractiveren, Exporteren, Verwijderen.
 * A choice is handed over as a `BulkRequest`; the page asks the question and runs it. Archiving
 * asks nothing, since it can be undone; export is the page's own business.
 */
export function BulkMenus({
  object,
  register,
  options,
  archived,
  onRequest,
  onExport,
  selected,
  onDone,
}: {
  object: CrmObject
  register: Register
  options: FilterOptions
  /** The view lists archived records: the status button reactivates instead. */
  archived: boolean
  onRequest: (request: BulkRequest) => void
  onExport: () => void
  /** What is ticked, for a page with a bulk action of its own (Uit lijst halen). */
  selected?: { ids: string[]; all: boolean; total: number }
  /** After such an action: clear the ticks and read the rows again. */
  onDone?: () => void
}) {
  const contacts = object === 'contacten'
  const [merging, setMerging] = useState(false)
  // Two ticked rows (not "Alle n selecteren" by query) can be merged.
  const pair = contacts && selected && !selected.all && selected.ids.length === 2 ? selected.ids : null
  // "Ik" and "Niemand" are for filters; a bulk owner is a member of the team.
  const team = choicesOf(register, options, 'owner')?.filter((choice) => choice.value !== 'me' && choice.value !== 'none') ?? null
  return (
    <>
      <MenuPopover label="Labels" onRequest={onRequest}>
        {(choose) => (
          <ChoiceList
            label="Labels"
            choices={choicesOf(register, options, 'labels')}
            row={(choice) => (
              <div key={choice.value} className="flex items-center gap-1 rounded-md px-2 py-0.5 hover:bg-muted">
                <span className="min-w-0 flex-1 text-sm break-words">{choice.label}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label={`${choice.label} toevoegen`}
                  onClick={() => choose(ask('labels', { add_label_ids: [choice.value], remove_label_ids: [] }, `label '${choice.label}' toevoegen`))}
                >
                  Toevoegen
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label={`${choice.label} weghalen`}
                  onClick={() => choose(ask('labels', { add_label_ids: [], remove_label_ids: [choice.value] }, `label '${choice.label}' weghalen`))}
                >
                  Weghalen
                </Button>
              </div>
            )}
          />
        )}
      </MenuPopover>
      <MenuPopover label="Categorie" onRequest={onRequest}>
        {(choose) => (
          <ChoiceList
            label="Categorie"
            choices={choicesOf(register, options, 'category')}
            lead={<ChoiceButton onClick={() => choose(ask('category', { category_id: null }, 'categorie weghalen'))}>Geen categorie</ChoiceButton>}
            row={(choice) => (
              <ChoiceButton key={choice.value} onClick={() => choose(ask('category', { category_id: choice.value }, `categorie '${choice.label}' zetten`))}>
                {choice.label}
              </ChoiceButton>
            )}
          />
        )}
      </MenuPopover>
      <MenuPopover label="Eigenaar" onRequest={onRequest}>
        {(choose) => <OwnerChoices team={team} choose={choose} />}
      </MenuPopover>
      {contacts ? (
        <>
          <MenuPopover label="Aan lijst" onRequest={onRequest}>
            {(choose) => (
              <ChoiceList
                label="Aan lijst"
                choices={choicesOf(register, options, 'lists')}
                row={(choice) => (
                  <ChoiceButton key={choice.value} onClick={() => choose(ask('list', { list_id: choice.value }, `toevoegen aan lijst '${choice.label}'`))}>
                    {choice.label}
                  </ChoiceButton>
                )}
              />
            )}
          </MenuPopover>
          <MenuPopover label="Organisatie koppelen" wide onRequest={onRequest}>
            {(choose) => <OrganisationForm options={options} choose={choose} />}
          </MenuPopover>
          <MenuPopover label="Contactritme instellen" wide onRequest={onRequest}>
            {(choose) => <RhythmForm choose={choose} />}
          </MenuPopover>
        </>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          onRequest({
            action: 'status',
            params: { status: archived ? 'actief' : 'inactief' },
            summary: archived ? 'heractiveren' : 'archiveren',
            confirm: !archived,
            destructive: false,
            ...(archived ? {} : { note: 'Lopende afspraken blijven doorlopen.' }),
          })
        }
      >
        {archived ? 'Heractiveren' : 'Archiveren'}
      </Button>
      {pair ? (
        <Button type="button" variant="outline" size="sm" onClick={() => setMerging(true)}>
          Samenvoegen…
        </Button>
      ) : null}
      {merging && pair ? (
        <MergeContactsDialog
          firstId={pair[0]}
          secondId={pair[1]}
          onClose={() => setMerging(false)}
          onMerged={() => {
            setMerging(false)
            onDone?.()
          }}
          onUndone={onDone}
        />
      ) : null}
      <Button type="button" variant="outline" size="sm" onClick={onExport}>
        Exporteren
      </Button>
      <Button type="button" variant="destructive" size="sm" onClick={() => onRequest({ action: 'delete', params: {}, summary: 'verwijderen', confirm: true, destructive: true })}>
        Verwijderen
      </Button>
    </>
  )
}
